/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, Send, Sparkles, AlertCircle, ArrowRight, MessageSquare, Loader2 } from "lucide-react";
import API from "../lib/api";
import BRAND from "../constants/brand";
import { errorMessage } from "../lib/errors";

/**
 * ============================================================
 * AskDonnyConcierge — global, persistent, context-aware AI bubble
 * ============================================================
 *
 * UX rules (locked with user):
 *   • Visible on every page EXCEPT:
 *       /login, /signup, /auth/callback (and any /auth/* path)
 *       /shop/success, /shop/checkout, /checkout (payment screens)
 *   • Premium black + gold drawer
 *   • Rounded corners, slides from bottom-right
 *   • Mobile responsive: near-full-screen on small viewports
 *   • Context-aware: passes pathname + page label to /api/coach/chat
 *   • Persistent across pages: conversation_id stored in sessionStorage
 *
 * The component owns its own chat state.  Recommendations and crisis
 * warnings render in-line in the drawer (no separate aside) to keep
 * the surface uncluttered yet useful.
 */

// ---- Surface guard
const HIDDEN_EXACT = new Set([
  "/login",
  "/signup",
  "/auth/callback",
  "/auth/reset",
  "/shop/success",
  "/shop/checkout",
  "/checkout",
]);
const HIDDEN_PREFIXES = ["/auth/", "/checkout/", "/shop/success", "/shop/checkout"];

function shouldHide(pathname) {
  if (HIDDEN_EXACT.has(pathname)) return true;
  return HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));
}

// ---- Page → topic label inference (matches the IA)
const PAGE_LABELS = [
  { test: /^\/wellness\/mens/, label: "Men's Wellness", topic: "mens-wellness" },
  { test: /^\/wellness\/womens/, label: "Women's Wellness", topic: "womens-wellness" },
  { test: /^\/wellness\/mental/, label: "Mental Wellness", topic: "mental-wellness" },
  { test: /^\/wellness\/nutrition/, label: "Nutrition", topic: "nutrition" },
  { test: /^\/wellness\/sleep/, label: "Sleep", topic: "sleep" },
  { test: /^\/wellness\/sports/, label: "Sports Performance", topic: "sports-performance" },
  { test: /^\/wellness/, label: "Wellness", topic: "wellness" },
  { test: /^\/relationships\/luv-connect/, label: "Luv Connect", topic: "luv-connect" },
  { test: /^\/relationships\/couples-connect/, label: "Couples Connect", topic: "couples-connect" },
  { test: /^\/relationships\/healthy/, label: "Healthy Relationships", topic: "healthy-relationships" },
  { test: /^\/relationships\/communication/, label: "Communication", topic: "communication" },
  { test: /^\/relationships\/dating/, label: "Dating", topic: "dating" },
  { test: /^\/relationships\/wellness/, label: "Relationship Wellness", topic: "relationship-wellness" },
  { test: /^\/relationships/, label: "Relationships", topic: "relationships" },
  { test: /^\/families\/parenting/, label: "Parenting", topic: "parenting" },
  { test: /^\/families\/wellness/, label: "Family Wellness", topic: "family-wellness" },
  { test: /^\/families\/teens/, label: "Teens & Youth", topic: "teens-youth" },
  { test: /^\/teens/, label: "Teens & Youth", topic: "teens-youth" },
  { test: /^\/families/, label: "Families & Parenting", topic: "families-parenting" },
  { test: /^\/business\/partners/, label: "Partner Businesses", topic: "partner-businesses" },
  { test: /^\/business\/corporate/, label: "Corporate Wellness", topic: "corporate-wellness" },
  { test: /^\/business\/offers/, label: "Special Offers", topic: "special-offers" },
  { test: /^\/business\/directory/, label: "Business Directory", topic: "business-directory" },
  { test: /^\/business/, label: "Business & Corporate", topic: "business" },
  { test: /^\/marketplace/, label: "Wellness Marketplace", topic: "marketplace" },
  { test: /^\/shop/, label: "DONiTSH!T Shop", topic: "shop" },
  { test: /^\/dosha/, label: "Dosha Test", topic: "dosha" },
  { test: /^\/article/, label: "Wellness Article", topic: "article" },
  { test: /^\/profile/, label: "Profile", topic: "profile" },
  { test: /^\/support/, label: "Support", topic: "support" },
  { test: /^\/ask-donny/, label: "Ask Donny", topic: "ask-donny" },
  { test: /^\/$/, label: "Homepage", topic: "ecosystem" },
];

function getPageContext(pathname) {
  for (const row of PAGE_LABELS) {
    if (row.test.test(pathname)) {
      return { label: row.label, topic: row.topic, pathname };
    }
  }
  return { label: "DONiTSH!T", topic: "ecosystem", pathname };
}

// ---- Context-aware starter prompts
function starterPromptsFor(label) {
  const base = {
    "Men's Wellness": [
      "What habits boost energy and recovery for men?",
      "How do I balance work, family, and fitness?",
      "What's a good morning routine for men?",
    ],
    "Women's Wellness": [
      "How do hormones affect my energy through the month?",
      "What rituals support women's mental wellbeing?",
      "Help me build a sustainable self-care routine.",
    ],
    "Mental Wellness": [
      "How do I manage chronic stress without medication?",
      "What's the difference between burnout and anxiety?",
      "Suggest a 5-minute reset I can do at my desk.",
    ],
    "Nutrition": [
      "How do I eat for my dosha?",
      "What's a balanced day on a plate?",
      "Quick anti-inflammatory meal ideas?",
    ],
    "Sleep": [
      "How do I fix a broken sleep schedule?",
      "What's a wind-down routine that actually works?",
      "Tell me about sleep and the doshas.",
    ],
    "Sports Performance": [
      "How should I structure recovery for high training loads?",
      "What's smart hydration for endurance?",
      "Talk to me about overtraining warning signs.",
    ],
    "Relationships": [
      "How do I communicate boundaries with my partner?",
      "What are signs of a healthy relationship?",
      "I want to reconnect with my partner — where do I start?",
    ],
    "Couples Connect": [
      "Give us a weekend ritual that deepens connection.",
      "How do we keep intimacy alive long-term?",
      "Tips for emotional safety in conversation?",
    ],
    "Luv Connect": [
      "How do I show up for myself before dating?",
      "What's a healthy way to start a new relationship?",
      "Talk to me about attachment styles.",
    ],
    "Parenting": [
      "How do I stay calm during meltdowns?",
      "Tips for raising emotionally aware kids.",
      "Help me build a screen-time policy that works.",
    ],
    "Family Wellness": [
      "Give us a family wellness Sunday plan.",
      "How do we make wellbeing a shared family habit?",
      "Suggest meaningful, screen-free family rituals.",
    ],
    "Teens & Youth": [
      "How do I support a teen with anxiety?",
      "Talk to me about social media and self-esteem.",
      "Healthy habits for student-athletes.",
    ],
    "Business & Corporate": [
      "What does a great corporate wellness program look like?",
      "How can my company benefit from the DONiTSH!T marketplace?",
      "Tips for leading a healthier team.",
    ],
    "DONiTSH!T Shop": [
      "Which program fits a beginner like me?",
      "Recommend a digital course based on my dosha.",
      "What's the difference between Vata, Pitta, and Kapha programs?",
    ],
    "Wellness Marketplace": [
      "Find me a coach near me.",
      "Which practitioners help with anxiety and burnout?",
      "How are practitioners vetted?",
    ],
    "Dosha Test": [
      "Walk me through what a dosha is.",
      "How accurate is this test?",
      "What do I do once I get my result?",
    ],
    "Homepage": [
      "Give me a 3-step starting point inside DONiTSH!T.",
      "What's the best place to begin if I'm new?",
      "How does DONiTSH!T work as an ecosystem?",
    ],
  };
  return base[label] || [
    "Where should I start in the DONiTSH!T ecosystem?",
    "How can I take a small step today?",
    "Recommend something based on my dosha.",
  ];
}

const STORAGE_CONVO_KEY = "donitshit_donny_conversation_id";
const STORAGE_OPEN_HINT = "donitshit_donny_opened_once";

export default function AskDonnyConcierge() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(true); // first-load pulse
  const [unread, setUnread] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_CONVO_KEY) || ""; } catch { return ""; }
  });
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const context = useMemo(() => getPageContext(pathname), [pathname]);
  const hidden = shouldHide(pathname);

  // Stop the pulse once user has seen the bubble for the first time
  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_OPEN_HINT)) {
        setPulse(false);
      } else {
        const t = setTimeout(() => {
          setPulse(false);
          sessionStorage.setItem(STORAGE_OPEN_HINT, "1");
        }, 8000);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);

  // Persist conversation id
  useEffect(() => {
    try {
      if (conversationId) sessionStorage.setItem(STORAGE_CONVO_KEY, conversationId);
    } catch {}
  }, [conversationId]);

  // Scroll-to-bottom on new message
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending, open]);

  // Auto-focus input when drawer opens
  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 350);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Lock body scroll on mobile while drawer is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    if (window.innerWidth < 768) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const send = async (preset) => {
    const message = (preset ?? text).trim();
    if (!message || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: message }]);
    setText("");
    try {
      const { data } = await API.post("/coach/chat", {
        message,
        conversation_id: conversationId || undefined,
        language: i18n.language,
        page_context: {
          pathname: context.pathname,
          page_label: context.label,
          topic: context.topic,
        },
      });
      setConversationId(data.conversation_id);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: data.answer,
          recommendations: data.recommendations,
          crisis_detected: data.crisis_detected,
        },
      ]);
      if (!open) setUnread(true);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "⚠️ " + errorMessage(e, t("ask_donny.error_again")) },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setUnread(false);
    setPulse(false);
    try { sessionStorage.setItem(STORAGE_OPEN_HINT, "1"); } catch {}
  };

  if (hidden) return null;

  // Localized page label from translation dictionary; fallback to English from PAGE_LABELS
  const localizedLabel = t(`ask_donny.context_labels.${context.topic}`, context.label);
  const starters = (t(`ask_donny.starters.${context.topic}`, { returnObjects: true, defaultValue: starterPromptsFor(context.label) }) || []);

  return (
    <>
      {/* ============================================================ */}
      {/* FLOATING BUBBLE */}
      {/* ============================================================ */}
      {!open && (
        <button
          type="button"
          onClick={handleOpen}
          data-testid="ask-donny-bubble"
          aria-label={t("ask_donny.aria_bubble", { label: localizedLabel })}
          className="fixed z-[100] bottom-20 right-5 group inline-flex items-center gap-2.5
                     pl-2 pr-4 py-2 rounded-full
                     bg-gradient-to-br from-[#0E0F14] via-[#15161D] to-[#0A0A0F]
                     border border-[#C8A24A]/45
                     shadow-[0_18px_50px_-12px_rgba(212,175,55,0.55)]
                     hover:shadow-[0_22px_60px_-10px_rgba(212,175,55,0.75)]
                     hover:border-[#E2C77A]/60
                     transition-all duration-300
                     hover:-translate-y-0.5"
        >
          {/* Halo pulse */}
          {pulse && (
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full ring-2 ring-[#D4AF37]/40 animate-ping pointer-events-none"
            />
          )}
          <span className="relative shrink-0">
            <img
              src={BRAND.DONNY_AVATAR}
              alt=""
              className="w-10 h-10 rounded-full object-cover ring-2 ring-[#C8A24A]/60"
              loading="lazy"
              data-testid="ask-donny-bubble-avatar"
            />
            {/* Status dot */}
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#0A0A0F]" />
          </span>
          <span className="hidden sm:flex flex-col items-start leading-tight pr-1">
            <span className="text-[10px] tracking-[0.22em] uppercase gold-text">{t("ask_donny.title")}</span>
            <span className="text-xs text-white/82 max-w-[160px] truncate">
              {context.topic === "ecosystem" ? t("ask_donny.bubble_concierge") : t("ask_donny.bubble_ask_about", { label: localizedLabel })}
            </span>
          </span>
          {unread && (
            <span
              className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#D4AF37] ring-2 ring-[#0A0A0F]"
              data-testid="ask-donny-unread-dot"
            />
          )}
        </button>
      )}

      {/* ============================================================ */}
      {/* SCRIM (mobile + desktop subtle) */}
      {/* ============================================================ */}
      {open && (
        <div
          role="presentation"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[98] bg-black/55 backdrop-blur-[2px] animate-in fade-in-0 duration-200"
          data-testid="ask-donny-scrim"
        />
      )}

      {/* ============================================================ */}
      {/* PREMIUM DRAWER */}
      {/* ============================================================ */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("ask_donny.title")}
        aria-hidden={!open}
        data-state={open ? "open" : "closed"}
        data-testid="ask-donny-drawer"
        className={`fixed z-[100] right-0 bottom-0 sm:right-5 sm:bottom-5
                    w-full sm:w-[460px] md:w-[500px]
                    h-[92vh] sm:h-[min(720px,calc(100vh-40px))]
                    transition-all duration-300 ease-out
                    ${open ? "translate-y-0 opacity-100 pointer-events-auto" : "translate-y-6 opacity-0 pointer-events-none"}`}
      >
        <div className="h-full flex flex-col bg-gradient-to-b from-[#0E0F14] via-[#0B0C12] to-[#080910]
                        border border-[#C8A24A]/35
                        rounded-t-2xl sm:rounded-2xl
                        shadow-[0_30px_120px_-20px_rgba(0,0,0,0.85),0_0_60px_-30px_rgba(212,175,55,0.55)]
                        overflow-hidden">

          {/* ---- Header */}
          <div className="relative">
            {/* Decorative gold gradient strip */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E2C77A]/70 to-transparent" />
            <div className="flex items-center gap-3 p-4 sm:p-5 border-b border-white/10">
              <div className="relative shrink-0">
                <img
                  src={BRAND.DONNY_AVATAR}
                  alt="Donny avatar"
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-[#C8A24A]/55"
                  loading="lazy"
                />
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#0B0C12]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg leading-none flex items-center gap-2">
                  Donny
                  <span className="text-[10px] tracking-[0.22em] uppercase gold-text border border-[#C8A24A]/30 rounded-full px-2 py-0.5">AI</span>
                </div>
                <div className="text-xs text-white/65 mt-1 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 gold-text" />
                  <span className="truncate" data-testid="ask-donny-context-label">
                    {t("ask_donny.context_label")}: <span className="text-white/85">{localizedLabel}</span>
                  </span>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label={t("ask_donny.aria_close")}
                data-testid="ask-donny-close"
                className="rounded-full p-2 text-white/70 hover:text-white hover:bg-white/5 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* ---- Body (messages + starters) */}
          <div
            ref={scrollRef}
            data-testid="ask-donny-thread"
            className="flex-1 overflow-y-auto px-4 sm:px-5 py-5 space-y-4"
          >
            {messages.length === 0 && (
              <div className="space-y-5">
                <div className="card-surface p-4 bg-gradient-to-br from-[#15161D] to-[#0A0A0F] border-[#C8A24A]/20">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E2C77A] to-[#7A5F2A] grid place-items-center shrink-0">
                      <Sparkles className="w-4 h-4 text-black" />
                    </div>
                    <div className="text-sm text-white/85 leading-relaxed">
                      {t("ask_donny.intro")}{" "}
                      <span className="gold-text font-medium">{localizedLabel}</span>.{" "}
                      {t("ask_donny.intro_2")}
                    </div>
                  </div>
                </div>

                <div data-testid="ask-donny-starters">
                  <div className="text-[10px] tracking-[0.28em] uppercase gold-text mb-2.5">
                    {t("ask_donny.starters_label")}
                  </div>
                  <div className="flex flex-col gap-2">
                    {starters.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => send(p)}
                        className="text-left text-sm px-4 py-3 rounded-xl border border-white/10
                                   bg-white/[0.03] hover:bg-white/[0.07] hover:border-[#C8A24A]/40
                                   transition-colors flex items-center gap-2 group"
                        data-testid={`ask-donny-starter-${i}`}
                      >
                        <span className="flex-1 text-white/85 group-hover:text-white">{p}</span>
                        <ArrowRight className="w-4 h-4 gold-text opacity-70 group-hover:opacity-100" />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-white/10 p-3.5 text-[11px] text-white/55 leading-relaxed">
                  {t("ask_donny.disclaimer")}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <Message key={i} message={m} />
            ))}

            {sending && (
              <div className="flex gap-3" data-testid="ask-donny-thinking">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E2C77A] to-[#7A5F2A] grid place-items-center shrink-0">
                  <Sparkles className="w-4 h-4 text-black" />
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tl-md px-4 py-3 flex gap-1.5 items-center">
                  <span className="w-2 h-2 rounded-full bg-[#D4AF37]/80 animate-pulse" />
                  <span className="w-2 h-2 rounded-full bg-[#D4AF37]/80 animate-pulse" style={{ animationDelay: "0.15s" }} />
                  <span className="w-2 h-2 rounded-full bg-[#D4AF37]/80 animate-pulse" style={{ animationDelay: "0.3s" }} />
                </div>
              </div>
            )}
          </div>

          {/* ---- Composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-white/10 p-3.5 bg-[#0A0A0F]/85 backdrop-blur"
            data-testid="ask-donny-composer"
          >
            <div className="flex items-end gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={1}
                  placeholder={t("ask_donny.placeholder")}
                  className="w-full resize-none bg-[#12131A] border border-white/10
                             focus:border-[#C8A24A]/55 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/15
                             rounded-xl px-3.5 py-3 text-sm text-white placeholder-white/40
                             max-h-32 transition-colors"
                  data-testid="ask-donny-input"
                />
              </div>
              <button
                type="submit"
                disabled={sending || !text.trim()}
                className="shrink-0 w-11 h-11 grid place-items-center rounded-xl
                           bg-gradient-to-br from-[#E8D199] via-[#D4AF37] to-[#7A5F2A]
                           text-black disabled:opacity-50 disabled:cursor-not-allowed
                           hover:shadow-[0_8px_24px_-6px_rgba(212,175,55,0.65)]
                           transition-shadow"
                aria-label={t("ask_donny.aria_send")}
                data-testid="ask-donny-send"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex items-center justify-between mt-2 text-[10px] text-white/45">
              <span className="inline-flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />
                {t("ask_donny.context_chip")}
              </span>
              <Link
                to="/ask-donny"
                className="hover:text-white/75 underline-offset-2 hover:underline"
                onClick={() => setOpen(false)}
                data-testid="ask-donny-fullpage-link"
              >
                {t("ask_donny.open_fullpage")}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// ============================================================
// Message bubble (with recommendations rendered inline)
// ============================================================
function Message({ message }) {
  const { t } = useTranslation();
  const isUser = message.role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : ""}`} data-testid={`donny-msg-${message.role}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E2C77A] to-[#7A5F2A] grid place-items-center shrink-0">
          <Sparkles className="w-4 h-4 text-black" />
        </div>
      )}
      <div className={`max-w-[80%] ${isUser ? "" : "space-y-2"}`}>
        <div
          className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words
            ${isUser
              ? "bg-gradient-to-br from-[#E8D199] via-[#D4AF37] to-[#B8941F] text-black rounded-tr-md"
              : "bg-white/[0.06] border border-white/10 text-white/90 rounded-tl-md"}`}
        >
          {message.content}
        </div>

        {message.crisis_detected && (
          <div className="flex items-center gap-2 text-rose-300 text-[11px] px-1">
            <AlertCircle className="w-3 h-3" />
            <span>{t("ask_donny.crisis_inline")}</span>
          </div>
        )}

        {!isUser && message.recommendations && (
          <Recommendations recs={message.recommendations} />
        )}
      </div>
    </div>
  );
}

function Recommendations({ recs }) {
  const { t } = useTranslation();
  if (!recs) return null;
  const sections = [];

  if (recs.articles?.length) {
    sections.push({
      key: "articles",
      label: t("ask_donny.recs.articles"),
      items: recs.articles.slice(0, 3).map((a) => ({
        id: a.id,
        title: a.title,
        href: `/article/${a.slug}`,
      })),
    });
  }
  if (recs.ebooks?.length) {
    sections.push({
      key: "ebooks",
      label: t("ask_donny.recs.ebooks"),
      items: recs.ebooks.slice(0, 2).map((a) => ({ id: a.id, title: a.title, href: "/shop" })),
    });
  }
  if (recs.workouts?.length) {
    sections.push({
      key: "workouts",
      label: t("ask_donny.recs.workouts"),
      items: recs.workouts.slice(0, 2).map((a) => ({ id: a.id, title: a.title, href: "/wellness" })),
    });
  }
  if (recs.workshops?.length) {
    sections.push({
      key: "workshops",
      label: t("ask_donny.recs.workshops"),
      items: recs.workshops.slice(0, 2).map((a) => ({ id: a.id, title: a.title, href: "/workshops" })),
    });
  }
  const crisis = recs.mental_health_resources?.length ? recs.mental_health_resources.slice(0, 3) : [];
  const offers = recs.offers?.length ? recs.offers.slice(0, 2) : [];

  if (sections.length === 0 && crisis.length === 0 && offers.length === 0) return null;

  return (
    <div className="rounded-xl border border-[#C8A24A]/20 bg-[#0A0A10]/75 p-3 space-y-3" data-testid="donny-recs">
      {offers.length > 0 && (
        <div className="border-b border-white/10 pb-2.5">
          <div className="text-[10px] tracking-[0.28em] uppercase gold-text mb-1.5 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> {t("ask_donny.recs.member_benefits")}
          </div>
          <ul className="space-y-1.5">
            {offers.map((o) => (
              <li key={o.offer_id}>
                <Link
                  to="/business/offers"
                  className="text-xs text-white/85 hover:text-white inline-flex items-start gap-1.5 hover:underline underline-offset-2"
                >
                  <ArrowRight className="w-3 h-3 gold-text mt-0.5" />
                  <span>
                    <span className="gold-text font-medium">{o.discount_label}</span>
                    {" — "}
                    {o.title}
                    {o.business?.name ? <span className="text-white/55"> · {o.business.name}</span> : null}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {sections.map((s) => (
        <div key={s.key}>
          <div className="text-[10px] tracking-[0.28em] uppercase gold-text mb-1.5">{s.label}</div>
          <ul className="space-y-1">
            {s.items.map((it) => (
              <li key={it.id}>
                <Link
                  to={it.href}
                  className="text-xs text-white/85 hover:text-white inline-flex items-center gap-1.5 hover:underline underline-offset-2"
                >
                  <ArrowRight className="w-3 h-3 gold-text" />
                  {it.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {crisis.length > 0 && (
        <div className="border-t border-white/10 pt-2.5">
          <div className="text-[10px] tracking-[0.28em] uppercase text-rose-300 mb-1.5 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {t("ask_donny.recs.crisis_support")}
          </div>
          <ul className="space-y-1.5">
            {crisis.map((r, i) => (
              <li key={i} className="text-xs text-white/85">
                <span className="font-medium">{r.name}</span>
                <span className="text-white/55"> · {r.phone || r.sms || r.url}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
